import { useQuery } from '@apollo/client';
import { useLogEvent } from 'hooks/useLogEvent';
import {
  ESiteTableColumnKeys,
  TSiteTableColumnKeys,
} from 'FeatureModules/Site/Components/SiteTable/SiteTable';
import { SITES_TABLE_ROWS_PER_PAGE } from 'utils/pagination.utils';
import { IOrderBy } from 'DesignSystem/DataDisplay/Table/table.types';
import { GET_BASE_PARTNER_COMPANY } from 'graphql/secure/queries/partner.queries';
import { GET_SITES } from 'graphql/secure/queries/site.queries';
import { ISiteSummary } from 'graphql/secure/queries/types/site-query.types';
import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { IBasePartnerCompany } from 'FeatureModules/Partner/types/partner.types';
import { TRouteParamsWithId } from 'types/router.types';
import { ESiteViewOption, IBaseSite } from 'FeatureModules/Site/types/site.types';
import { IGraphQlConnection } from 'types/core.types';
import { StringParam, createEnumParam, useQueryParams, withDefault } from 'use-query-params';
import { removeGraphConnections } from 'utils/graphql.utils';

const usePartnerSites = () => {
  const { id } = useParams<TRouteParamsWithId>();
  const [query, setQuery] = useQueryParams(
    {
      searchTerm: withDefault(StringParam, ''),
      viewOption: withDefault(
        createEnumParam<ESiteViewOption>([ESiteViewOption.CLUSTER, ESiteViewOption.LIST]),
        ESiteViewOption.CLUSTER
      ),
      orderByMethod: withDefault(createEnumParam<'ASC' | 'DESC'>(['ASC', 'DESC']), undefined),
      orderByField: withDefault(
        createEnumParam<TSiteTableColumnKeys>(Object.values(ESiteTableColumnKeys)),
        undefined
      ),
    },
    { removeDefaultsFromUrl: true }
  );

  const { searchTerm, viewOption, orderByMethod, orderByField } = query;

  const orderBy =
    orderByMethod && orderByField
      ? {
          orderByField: orderByField,
          orderByMethod: orderByMethod,
        }
      : undefined;

  const { logEvent } = useLogEvent();

  const { data, loading, error, fetchMore, refetch } = useQuery<{
    sites: IGraphQlConnection<IBaseSite, ISiteSummary>;
  }>(GET_SITES, {
    variables: {
      filters: {
        // We have to send null to get all sites since the default is INTERNAL
        ownership: null,
        ownedByIds: [id],
        ...(searchTerm ? { searchTerm: searchTerm } : {}),
      },
      ...(searchTerm || id
        ? {
            summaryFilters: {
              ...(searchTerm ? { searchTerm } : {}),
              ...(id
                ? {
                    ownedByIds: [id],
                  }
                : {}),
            },
          }
        : {}),
      orderBy,
      siteViewOption: viewOption,
      first: SITES_TABLE_ROWS_PER_PAGE,
    },
  });
  const { data: partnerData } = useQuery<{ partner: IBasePartnerCompany }>(
    GET_BASE_PARTNER_COMPANY,
    {
      variables: {
        id,
      },
    }
  );

  const partner = partnerData?.partner;

  const sites: IBaseSite[] = useMemo(
    () => (data?.sites ? removeGraphConnections(data.sites) : []),
    [data]
  );

  const hasNextPage: boolean = !!data?.sites?.pageInfo?.hasNextPage;

  const endCursor: string | undefined = data?.sites?.pageInfo?.endCursor;

  const handlePageEndReached = () => {
    if (endCursor) {
      fetchMore({
        variables: {
          after: endCursor,
        },
      });
    }
  };

  useEffect(() => {
    if (!sites?.length && hasNextPage) {
      handlePageEndReached();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sites, hasNextPage]);

  const handleSearchTermChange = (searchTerm: string) => {
    logEvent('SITE_SEARCHED', { searchTerm });
    setQuery({ searchTerm });
  };

  const handleSiteViewOptionChange = (viewOption: ESiteViewOption) => {
    setQuery({ viewOption });
  };

  const handleOrderByChange = (newOrderBy?: IOrderBy<TSiteTableColumnKeys>) => {
    setQuery(prev => ({
      ...prev,
      orderByMethod: newOrderBy?.orderByMethod,
      orderByField: newOrderBy?.orderByField,
    }));
  };

  return {
    sites,
    partner,
    summary: data?.sites.summary,
    totalCount: data?.sites.count,
    loading,
    error,
    searchTerm,
    hasNextPage,
    viewOption,
    orderBy,
    refetch,
    handleOrderByChange,
    handleSiteViewOptionChange,
    handleSearchTermChange,
    handlePageEndReached,
  };
};

export default usePartnerSites;
